import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api, { getImageUrl } from '../../api';
import { productSlug } from '../../utils/slug';
import AdminLayout from '../../components/AdminLayout';

export default function AdminProducts() {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const autoEditId = urlParams.get('edit');
  const autoVariantsId = urlParams.get('variants');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const LIMIT = 50;
  const [form, setForm] = useState({
    sku: '', name: '', description: '', price: '', compare_at_price: '', cost_price: '',
    category_id: '', brand: '', stock_quantity: '0', low_stock_threshold: '5',
    weight: '', dimensions: '', tags: '', gender: '', style: '', material: '', color: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  // Variant management
  const [variantProduct, setVariantProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [variantForm, setVariantForm] = useState({ attributes: {}, price_adjustment: 0, stock_quantity: 0, sku: '' });
  const [newAttrKey, setNewAttrKey] = useState('');
  const [newAttrVal, setNewAttrVal] = useState('');

  const loadProducts = () => {
    const params = new URLSearchParams({ active_only: '0', limit: LIMIT, page });
    if (search) params.set('search', search);
    api.get(`/products?${params}`).then(r => {
      setProducts(r.data.products);
      setPagination(r.data.pagination);
      setLoading(false);
    });
  };

  useEffect(() => { setPage(1); }, [search]);
  useEffect(() => { loadProducts(); }, [search, page]);
  useEffect(() => { api.get('/categories').then(r => setCategories(r.data)); }, []);

  // Auto-open edit or variants modal from URL params
  useEffect(() => {
    if (!autoEditId && !autoVariantsId) return;
    const targetId = parseInt(autoEditId || autoVariantsId);
    api.get(`/products/${targetId}`).then(r => {
      const p = r.data;
      if (autoEditId) {
        openEdit(p);
      } else {
        openVariants(p);
      }
    }).catch(() => {});
  }, [autoEditId, autoVariantsId]);

  const openNew = () => {
    setEditing(null);
    setForm({ sku: '', name: '', description: '', price: '', compare_at_price: '', cost_price: '',
      category_id: '', brand: '', stock_quantity: '0', low_stock_threshold: '5', weight: '', dimensions: '', tags: '',
      gender: '', style: '', material: '', color: '' });
    setImageFile(null);
    setAdditionalFiles([]);
    setExistingImages([]);
    setShowModal(true);
  };

  const openVariants = async (p) => {
    setVariantProduct(p);
    const r = await api.get(`/products/${p.id}/variants`);
    setVariants(r.data);
    setVariantForm({ attributes: {}, price_adjustment: 0, stock_quantity: 0, sku: '' });
    setNewAttrKey(''); setNewAttrVal('');
  };

  const addVariantAttr = () => {
    if (!newAttrKey.trim() || !newAttrVal.trim()) return;
    setVariantForm(f => ({ ...f, attributes: { ...f.attributes, [newAttrKey.trim()]: newAttrVal.trim() } }));
    setNewAttrKey(''); setNewAttrVal('');
  };

  const saveVariant = async () => {
    if (Object.keys(variantForm.attributes).length === 0) { alert('Add at least one attribute (e.g. Color: Red)'); return; }
    await api.post(`/products/${variantProduct.id}/variants`, variantForm);
    const r = await api.get(`/products/${variantProduct.id}/variants`);
    setVariants(r.data);
    setVariantForm({ attributes: {}, price_adjustment: 0, stock_quantity: 0, sku: '' });
    setNewAttrKey(''); setNewAttrVal('');
  };

  const deleteVariant = async (vid) => {
    if (!window.confirm('Delete this variant?')) return;
    await api.delete(`/products/${variantProduct.id}/variants/${vid}`);
    setVariants(v => v.filter(x => x.id !== vid));
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      sku: p.sku, name: p.name, description: p.description || '', price: p.price.toString(),
      compare_at_price: p.compare_at_price?.toString() || '', cost_price: p.cost_price?.toString() || '',
      category_id: p.category_id?.toString() || '', brand: p.brand || '',
      stock_quantity: p.stock_quantity.toString(), low_stock_threshold: p.low_stock_threshold.toString(),
      weight: p.weight?.toString() || '', dimensions: p.dimensions || '', tags: p.tags || '',
      gender: p.gender || '', style: p.style || '', material: p.material || '', color: p.color || ''
    });
    setImageFile(null);
    setAdditionalFiles([]);
    setExistingImages(p.images || []);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (v !== '') formData.append(k, v); });
    if (imageFile) formData.append('image', imageFile);
    additionalFiles.forEach(f => formData.append('additional_images', f));

    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/products', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setShowModal(false);
      loadProducts();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save product');
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!editing) return;
    try {
      await api.delete(`/products/${editing.id}/images/${imageId}`);
      setExistingImages(prev => prev.filter(img => img.id !== imageId));
    } catch (err) {
      alert('Failed to delete image');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await api.delete(`/products/${id}`);
    loadProducts();
  };

  const toggleActive = async (p) => {
    const formData = new FormData();
    formData.append('is_active', p.is_active ? '0' : '1');
    await api.put(`/products/${p.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    loadProducts();
  };

  return (
    <AdminLayout title="Products">
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <input
          type="text" placeholder="Search products..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, flex: 1, maxWidth: 300 }}
        />
        <button className="btn btn-primary" onClick={openNew}>+ Add Product</button>
      </div>

      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th></th><th>SKU</th><th>Name</th><th>Category</th><th>Price</th>
                <th>Cost</th><th>Stock</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id}>
                  <td>
                    {p.image_url ? (
                      <img src={getImageUrl(p.image_url)} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                    ) : (
                      <div style={{ width: 40, height: 40, background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📦</div>
                    )}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.sku}</td>
                  <td><strong>{p.name}</strong>{p.brand && <div style={{ fontSize: 12, color: '#999' }}>{p.brand}</div>}</td>
                  <td>{p.category_name || '-'}</td>
                  <td>${p.price.toFixed(2)}</td>
                  <td>{p.cost_price ? `$${p.cost_price.toFixed(2)}` : '-'}</td>
                  <td>
                    <span className={p.stock_quantity <= 0 ? 'out-of-stock' : p.stock_quantity <= p.low_stock_threshold ? 'low-stock' : 'in-stock'}>
                      {p.stock_quantity}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${p.is_active ? 'badge-delivered' : 'badge-cancelled'}`}>
                      {p.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {p.vip_only ? <span className="badge" style={{ background:'#ede9fe', color:'#4f46e5', marginLeft:4 }}>💎 VIP</span> : null}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Link to={`/product/${productSlug(p.name, p.id)}`} className="btn btn-sm btn-outline" target="_blank">View</Link>
                      <button className="btn btn-sm btn-outline" onClick={() => openEdit(p)}>Edit</button>
                      <button className="btn btn-sm btn-outline" onClick={() => openVariants(p)}>Variants</button>
                      <button className="btn btn-sm btn-outline" onClick={() => toggleActive(p)}>
                        {p.is_active ? 'Hide' : 'Show'}
                      </button>
                      <button className="btn btn-sm btn-outline" title={p.vip_only ? 'Make Public' : 'Make VIP Only'}
                        style={{ background: p.vip_only ? '#ede9fe' : 'white', color: p.vip_only ? '#4f46e5' : '#374151' }}
                        onClick={async () => {
                          const fd = new FormData(); fd.append('vip_only', p.vip_only ? '0' : '1');
                          await api.put(`/products/${p.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                          setProducts(prev => prev.map(x => x.id === p.id ? { ...x, vip_only: p.vip_only ? 0 : 1 } : x));
                        }}>
                        {p.vip_only ? '💎→Public' : '💎 VIP'}
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(p.id)}>Del</button>
                    </div>
                  </td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                  No products yet. Click "Add Product" to start building your inventory.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {pagination.pages > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginTop: 20, flexWrap: 'wrap' }}>
          <button className="btn btn-sm btn-outline" onClick={() => setPage(1)} disabled={page === 1}>«</button>
          <button className="btn btn-sm btn-outline" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹ Prev</button>
          <span style={{ padding: '6px 12px', fontSize: 14 }}>Page {page} of {pagination.pages} ({pagination.total} products)</span>
          <button className="btn btn-sm btn-outline" onClick={() => setPage(p => p + 1)} disabled={page === pagination.pages}>Next ›</button>
          <button className="btn btn-sm btn-outline" onClick={() => setPage(pagination.pages)} disabled={page === pagination.pages}>»</button>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editing ? 'Edit Product' : 'Add New Product'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>SKU *</label>
                  <input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} required placeholder="e.g. ELEC-001" />
                </div>
                <div className="form-group">
                  <label>Brand</label>
                  <input value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} placeholder="e.g. Apple" />
                </div>
              </div>
              <div className="form-group">
                <label>Product Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Product name" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Product description..." />
              </div>
              <div className="form-row-3">
                <div className="form-group">
                  <label>Selling Price *</label>
                  <input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} required placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label>Compare at Price</label>
                  <input type="number" step="0.01" value={form.compare_at_price} onChange={e => setForm({ ...form, compare_at_price: e.target.value })} placeholder="Original price" />
                </div>
                <div className="form-group">
                  <label>Cost Price</label>
                  <input type="number" step="0.01" value={form.cost_price} onChange={e => setForm({ ...form, cost_price: e.target.value })} placeholder="Your cost" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
                    <option value="">No Category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Main Image</label>
                  <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0])} />
                </div>
              </div>
              <div className="form-group">
                <label>Additional Images (up to 10)</label>
                <input type="file" accept="image/*" multiple onChange={e => setAdditionalFiles(Array.from(e.target.files))} />
                {additionalFiles.length > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{additionalFiles.length} file(s) selected</div>
                )}
              </div>
              {existingImages.length > 0 && (
                <div className="form-group">
                  <label>Existing Additional Images</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                    {existingImages.map(img => (
                      <div key={img.id} style={{ position: 'relative', width: 80, height: 80 }}>
                        <img src={getImageUrl(img.image_url)} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(img.id)}
                          style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: 20, height: 20, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                        >×</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Stock Quantity</label>
                  <input type="number" value={form.stock_quantity} onChange={e => setForm({ ...form, stock_quantity: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Low Stock Threshold</label>
                  <input type="number" value={form.low_stock_threshold} onChange={e => setForm({ ...form, low_stock_threshold: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Gender</label>
                  <select value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}>
                    <option value="">—</option>
                    <option value="Women">Women</option>
                    <option value="Men">Men</option>
                    <option value="Unisex">Unisex</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Style</label>
                  <input value={form.style} onChange={e => setForm({ ...form, style: e.target.value })} placeholder="e.g. Bohemian, Casual" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Material</label>
                  <input value={form.material} onChange={e => setForm({ ...form, material: e.target.value })} placeholder="e.g. Alloy, Cotton" />
                </div>
                <div className="form-group">
                  <label>Color</label>
                  <input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} placeholder="e.g. Gold, Black" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Weight (kg)</label>
                  <input type="number" step="0.01" value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Dimensions</label>
                  <input value={form.dimensions} onChange={e => setForm({ ...form, dimensions: e.target.value })} placeholder="e.g. 10x5x3 cm" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                <button type="submit" className="btn btn-primary">{editing ? 'Update Product' : 'Add Product'}</button>
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Variant Management Modal */}
      {variantProduct && (
        <div className="modal-overlay" onClick={() => setVariantProduct(null)}>
          <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <h2>Variants — {variantProduct.name}</h2>

            {/* Existing variants */}
            {variants.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20, fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Attributes</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Price adj.</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Stock</th>
                    <th style={{ padding: '8px 10px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map(v => (
                    <tr key={v.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px 10px' }}>
                        {Object.entries(v.attributes).map(([k,val]) => (
                          <span key={k} style={{ marginRight: 8, background: '#f0f0ff', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>
                            {k}: <strong>{val}</strong>
                          </span>
                        ))}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: v.price_adjustment > 0 ? '#ef4444' : v.price_adjustment < 0 ? '#10b981' : '#6b7280' }}>
                        {v.price_adjustment > 0 ? '+' : ''}{v.price_adjustment.toFixed(2)}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>{v.stock_quantity}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <button className="btn btn-sm btn-danger" onClick={() => deleteVariant(v.id)}>Del</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Add new variant */}
            <div style={{ background: '#f9fafb', borderRadius: 10, padding: 16 }}>
              <h4 style={{ marginBottom: 12 }}>Add Variant</h4>

              {/* Attribute builder */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Attributes (e.g. Color: Red, Size: M)</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  {Object.entries(variantForm.attributes).map(([k,v]) => (
                    <span key={k} style={{ background: '#e0e7ff', borderRadius: 6, padding: '4px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {k}: {v}
                      <button onClick={() => setVariantForm(f => { const a = {...f.attributes}; delete a[k]; return {...f, attributes: a}; })}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0, fontWeight: 700 }}>&times;</button>
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input placeholder="Key (e.g. Color)" value={newAttrKey} onChange={e => setNewAttrKey(e.target.value)}
                    style={{ flex: 1, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
                  <input placeholder="Value (e.g. Red)" value={newAttrVal} onChange={e => setNewAttrVal(e.target.value)}
                    style={{ flex: 1, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
                    onKeyDown={e => e.key === 'Enter' && addVariantAttr()} />
                  <button className="btn btn-outline btn-sm" onClick={addVariantAttr}>+ Add</button>
                </div>
                {/* Quick presets */}
                <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[['Size','XS'],['Size','S'],['Size','M'],['Size','L'],['Size','XL'],['Size','XXL'],['Size','One Size']].map(([k,v]) => (
                    <button key={v} onClick={() => setVariantForm(f => ({...f, attributes: {...f.attributes, [k]: v}}))}
                      style={{ fontSize: 11, padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: 100, background: variantForm.attributes[k]===v?'#c9a227':'white', color: variantForm.attributes[k]===v?'white':'#374151', cursor: 'pointer' }}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Stock</label>
                  <input type="number" value={variantForm.stock_quantity} min="0"
                    onChange={e => setVariantForm(f => ({...f, stock_quantity: parseInt(e.target.value)||0}))}
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Price Adj. ($)</label>
                  <input type="number" step="0.01" value={variantForm.price_adjustment} placeholder="0.00"
                    onChange={e => setVariantForm(f => ({...f, price_adjustment: parseFloat(e.target.value)||0}))}
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>SKU (optional)</label>
                  <input value={variantForm.sku} onChange={e => setVariantForm(f => ({...f, sku: e.target.value}))}
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
                </div>
              </div>

              <button className="btn btn-primary" onClick={saveVariant}>Add Variant</button>
            </div>

            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <button className="btn btn-outline" onClick={() => setVariantProduct(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
